export function setupDirectoryModule( container ) {
	const baseUrl = container.dataset.baseUrl;
	const fetchUrl = container.dataset.fetchUrl;

	const forms = container.querySelectorAll( 'form' );

	const liveArea = container.querySelector( '[aria-live]' ) ?? container.closest( '[aria-live]' );
	const heading = container.querySelector( '.js-directory-heading' );
	const filterWrappers = container.querySelectorAll( '.js-filter-group' );
	const statsWrapper = container.querySelector( '.js-directory-stats' );
	const sortWrapper = container.querySelector( '.js-directory-sort' );
	const navWrapper = container.querySelector( '.js-directory-nav' );
	const tagsWrapper = container.querySelector( '.js-directory-tags' );
	const resultsWrapper = container.querySelector( '.js-directory-results' );
	const paginationWrappers = container.querySelectorAll( '.js-directory-pagination' );
	const clearButton = container.querySelector( '.js-filter-clear' );

	const format = resultsWrapper.dataset.format;
	const perPage = paginationWrappers[ 0 ]?.dataset.perpage;

	const initParams = new URLSearchParams( window.location.search );
	if ( paginationWrappers[ 0 ] ) {
		initParams.append( 'paged', paginationWrappers[ 0 ].dataset.page );
	}

	history.replaceState( { query: initParams.toString() }, '' );

	function syncFormsWithParams( params ) {
		// Reset, but harder (e.g. from loading page with preselected options)
		container.querySelectorAll( 'input' ).forEach( input => {
			if ( input.type === 'radio' || input.type === 'checkbox' ) {
				input.checked = false;
			} else if ( input.type !== 'hidden' ) {
				input.value = '';
			}
		} );

		filterWrappers.forEach( group => group.style.setProperty( '--filter-count', '' ) );

		for ( const key of params.keys() ) {
			const selected = params.getAll( key );
			container.querySelectorAll( `[name="${ key }"]` ).forEach( input => {
				const group = input.closest( '.js-filter-group' );
				if ( group ) {
					group.style.setProperty( '--filter-count', selected.length ? `'${ selected.length }'` : '' );
				}

				if ( input.type === 'radio' || input.type === 'checkbox' ) {
					input.checked = selected.includes( input.value ) || ( selected.length === 0 && input.value === '' );
				} else if ( input.type !== 'hidden' ) {
					input.value = selected[ 0 ];
				}
			} );
		}
	}

	function fetchResults( params ) {
		syncFormsWithParams( params );

		params.append( 'format', format );

		container.classList.add( 'loading' );

		return fetch( fetchUrl + '?' + params.toString() )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `Directory Request Status: ${ response.status }` );
				}

				return response.json();
			} )
			.then( response => {
				if ( heading ) {
					heading.hidden = ! response.heading;
					heading.innerHTML = response.heading;
				}

				if ( statsWrapper ) {
					statsWrapper.hidden = ! response.stats;
					statsWrapper.innerHTML = response.stats;
				}

				if ( sortWrapper ) {
					sortWrapper.hidden = ! response.sort;
					sortWrapper.innerHTML = response.sort;
				}

				if ( navWrapper ) {
					navWrapper.hidden = ! response.nav;
					navWrapper.innerHTML = response.nav;
				}

				if ( tagsWrapper ) {
					tagsWrapper.hidden = ! response.tags;
					tagsWrapper.innerHTML = response.tags;
				}

				if ( resultsWrapper ) {
					let results = response.results;
					if ( results instanceof Array ) {
						results = results.join( '\n' );
					}

					resultsWrapper.hidden = ! results;
					resultsWrapper.innerHTML = results;
				}

				paginationWrappers.forEach( wrapper => {
					wrapper.hidden = ! response.pagination;
					wrapper.innerHTML = response.pagination;
				} );

				container.classList.remove( 'loading' );
			} )
			.catch( () => {
				// do nothing
			} )
			.finally( () => {
				liveArea.setAttribute( 'aria-busy', false );
				liveArea.focus();

				if ( window.scrollY > container.offsetTop ) {
					container.scrollIntoView( true );
				}

				// Move any modals within to body
				container.querySelectorAll( '.js-modal' ).forEach( modal => {
					// Remove if already present
					if ( document.querySelectorAll( `#${ modal.id }` ).length > 1 ) {
						modal.remove();
					} else {
						document.body.appendChild( modal );
					}
				} );
			} );
	}

	function fetchResultsFromForms( page = 1 ) {
		liveArea.setAttribute( 'aria-busy', true );

		const params = new URLSearchParams();
		forms.forEach( form => {
			const data = new FormData( form );
			for ( const [ key, value ] of data.entries() ) {
				if ( value ) {
					params.append( key, value );
				}
			}
		} );

		if ( page > 1 ) {
			params.append( 'paged', page );
		}

		if ( perPage ) {
			params.append( 'per_page', perPage );
		}

		const query = params.toString();
		if ( baseUrl ) {
			history.pushState( { query }, '', baseUrl + ( query ? `?${ query }` : '' ) );
		}

		fetchResults( params );
	}

	clearButton?.addEventListener( 'click', event => {
		event.preventDefault();
		filterWrappers.forEach( filter => {
			filter.querySelectorAll( '[name]' ).forEach( input => {
				if ( input.type === 'checkbox' || input.type === 'radio' ) {
					input.checked = false;
				} else {
					input.value = '';
				}
			} );
		} );
		fetchResultsFromForms();
	} );

	forms.forEach( form => {
		form.addEventListener( 'submit', event => {
			event.preventDefault();
			fetchResultsFromForms();
		} );
	} );

	filterWrappers.forEach( filter => {
		filter.addEventListener( 'input', event => {
			const form = event.target.closest( 'form' );
			if ( typeof form.requestSubmit === 'function' ) {
				form.requestSubmit();
			} else {
				form.dispatchEvent( new Event( 'submit' ) );
			}
		} );
	} );

	container.addEventListener( 'click', event => {
		const target = event.target.closest( 'a, button' ) || event.target;

		if ( target.dataset.page ) {
			event.preventDefault();
			fetchResultsFromForms( target.dataset.page );
		} else if ( target.dataset.filter ) {
			event.preventDefault();

			const [ name, value = null ] = target.dataset.filter.split( ':' );
			container.querySelectorAll( `[name="${ name }"], [name="${ name }[]"]` ).forEach( input => {
				input.checked = input.value === value;
			} );

			fetchResultsFromForms();
		} else if ( target.dataset.clear ) {
			event.preventDefault();

			const [ name, value = null ] = target.dataset.clear.split( ':' );

			let inputs = container.querySelectorAll( 'input.js-clearable' );
			if ( name !== 'ALL' ) {
				inputs = container.querySelectorAll( `[name="${ name }"], [name="${ name }[]"]` );
			}

			inputs.forEach( input => {
				if ( value === null || input.value === value ) {
					if ( input.type === 'checkbox' || input.type === 'radio' ) {
						input.checked = false;
					} else {
						input.value = '';
					}
				}
			} );

			fetchResultsFromForms();
		}
	} );

	window.addEventListener( 'popstate', event => {
		if ( event.state.query ) {
			event.preventDefault();
			const params = new URLSearchParams( event.state.query );
			fetchResults( params );
		}
	} );
}

export default function setupDirectoryModules() {
	document.querySelectorAll( '.js-directory' ).forEach( setupDirectoryModule );
}
